import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';

const AUDIT_SECTIONS = [
  {
    n: '1',
    title: 'Profile audit',
    items: [
      'Profile photo is clear, high-quality, and on-brand',
      'Username is memorable and consistent with your other platforms',
      'Bio clearly explains who you are and what you post (under 150 characters)',
      'Bio link is working and points to where you want traffic',
      'Highlights are organized, have custom covers, and represent your best content',
      'Contact info (email, phone) is up to date if you\'re a business account',
    ],
  },
  {
    n: '2',
    title: 'Content audit',
    items: [
      'Your last 9 posts look cohesive as a grid',
      'Post captions are engaging and include a call to action',
      'You\'re using relevant hashtags (not too many, not too few — 5–15 is a sweet spot)',
      'You\'re posting Reels (Instagram\'s algorithm heavily favors video)',
      'Posting frequency is consistent (at least 2–3x per week)',
      'Your best-performing posts hint at what your audience wants more of',
    ],
  },
  {
    n: '3',
    title: 'Following list audit',
    items: [
      'Check who you follow that doesn\'t follow you back (use Purgr)',
      'Unfollow inactive accounts (haven\'t posted in 6+ months)',
      'Unfollow accounts that no longer match your interests or niche',
      'Your following count is reasonable relative to your followers',
      'You\'re not following a large number of accounts just for follow-backs',
    ],
  },
  {
    n: '4',
    title: 'Engagement audit',
    items: [
      'Your engagement rate is healthy (1–3% is typical for personal; 0.5–1% for larger accounts)',
      'You\'re responding to comments within 24 hours',
      'You\'re engaging with accounts in your niche — not just waiting for engagement',
      'Your DMs are organized and you\'re not ignoring potential collaborations or inquiries',
      'You\'re using Stories to drive engagement between posts',
    ],
  },
];

const InstagramAccountAudit: React.FC = () => {
  useSEO(
    'How to Do a Full Instagram Account Audit (Free Checklist) — Purgr',
    'A complete Instagram account audit checklist covering your profile, content, following list, and engagement. Clean up and optimize your account in under 10 minutes.'
  );

  return (
    <PageLayout title="How to Do a Full Instagram Account Audit">
      <div className="space-y-8 text-white/70 text-sm leading-relaxed">

        <p className="text-white/80 text-base">
          An Instagram audit is a systematic review of your profile, content, following list, and
          engagement metrics. It helps you identify what's working, what's hurting your growth, and
          what to clean up. Here's a complete checklist you can work through in under 10 minutes.
        </p>

        {/* Why audit */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-2">Why do an Instagram audit?</p>
          <p className="text-white/55 text-xs leading-relaxed">
            Most Instagram accounts accumulate clutter over time — old bio links, inconsistent content,
            a bloated following list, and forgotten highlights. A regular audit (every 3–6 months)
            helps you stay intentional about your presence and gives the algorithm a cleaner signal
            about who you are and what you post.
          </p>
        </div>

        {/* Audit sections */}
        {AUDIT_SECTIONS.map(({ n, title, items }) => (
          <div key={n}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full ig-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {n}
              </div>
              <p className="text-white/90 font-semibold text-base">{title}</p>
            </div>
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item} className="glass-card p-3 flex items-start gap-3">
                  <span className="w-4 h-4 rounded border border-white/20 flex-shrink-0 mt-0.5" />
                  <p className="text-white/60 text-xs leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
            {n === '3' && (
              <p className="text-white/40 text-xs mt-3 ml-1">
                💡 Use{' '}
                <Link to="/" className="text-white/60 underline hover:text-white/80 transition-colors">
                  Purgr
                </Link>{' '}
                to quickly identify everyone you follow who doesn't follow you back — free, no login required.
              </p>
            )}
          </div>
        ))}

        {/* How often */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-3">How often should you audit your account?</p>
          <ul className="space-y-2 text-white/55 text-xs">
            <li>• <strong className="text-white/70">Every 3 months</strong> — following list cleanup and engagement review</li>
            <li>• <strong className="text-white/70">Every 6 months</strong> — full audit including profile, bio, and highlights</li>
            <li>• <strong className="text-white/70">After a rebrand or niche shift</strong> — review everything for consistency</li>
            <li>• <strong className="text-white/70">After a growth stall</strong> — diagnose what might be limiting your reach</li>
          </ul>
        </div>

        {/* CTA */}
        <div className="rounded-xl p-px ig-gradient-bg">
          <div className="rounded-xl bg-[#0D0D0D] px-6 py-6 text-center">
            <p className="text-white font-bold text-base mb-1">Start your following list audit now</p>
            <p className="text-white/50 text-sm mb-4">See who doesn't follow you back — free, private, no login needed.</p>
            <Link
              to="/"
              className="btn-gradient inline-block text-white font-bold px-8 py-3 rounded-full"
            >
              Try Purgr Free →
            </Link>
          </div>
        </div>

        {/* Related guides */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Related guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/how-to-improve-instagram-follower-ratio" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to improve your follower ratio →</p>
            </Link>
            <Link to="/how-to-clean-up-your-following-list" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to clean up your following list →</p>
            </Link>
            <Link to="/best-free-instagram-unfollow-tracker" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">Best free Instagram unfollow tracker →</p>
            </Link>
            <Link to="/what-is-a-good-follower-following-ratio" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">What is a good follower ratio? →</p>
            </Link>
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default InstagramAccountAudit;
